"use client"

import React, { useState, useRef, useEffect } from "react"
import Image from "next/image"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/shared/components/ui/dialog"
import { Button } from "@/shared/components/ui/button"
import { Label } from "@/shared/components/ui/label"
import { X, Play, FileText } from "lucide-react"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/components/ui/select"
import {
    galleryIcon,
} from "@/features/work-orders/constants/images"
import { getAllWorkOrders } from "@/features/work-orders/api/work-orders.api"
import { useSession } from "@/features/auth/context/session.context"
import successToast from "@/shared/lib/toast/success.toast"
import errorToast from "@/shared/lib/toast/error.toast"
import { useUploadQueue } from "@/features/photos/hooks/use-upload-queue"

// Stable empty array for default initialFiles; prevents new reference on each render
const EMPTY_FILE_ARRAY: File[] = []

// Component to handle individual file preview with proper cleanup
function FilePreviewItem({ file, isVideo, isPdf, onRemove }: { file: File; isVideo: boolean; isPdf?: boolean; onRemove: () => void }) {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)

    useEffect(() => {
        const url = URL.createObjectURL(file)
        setPreviewUrl(url)
        
        return () => {
            URL.revokeObjectURL(url)
        }
    }, [file])

    if (!previewUrl && !isPdf) return null

    return (
        <div className="relative group">
            {isPdf ? (
                <div className="w-full h-20 rounded border overflow-hidden bg-muted flex items-center justify-center gap-2 p-2">
                    <FileText className="w-8 h-8 text-muted-foreground" />
                    <span className="text-xs truncate">{file.name}</span>
                </div>
            ) : isVideo ? (
                <div className="relative w-full h-20 rounded border overflow-hidden bg-black">
                    <video
                        src={previewUrl!}
                        className="w-full h-full object-cover"
                        muted
                    />
                    {/* Video indicator */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                        <Play className="w-8 h-8 text-white fill-white" />
                    </div>
                </div>
            ) : (
                <Image
                    src={previewUrl!}
                    alt={`Preview ${file.name}`}
                    className="w-full h-20 object-cover rounded border"
                    width={80}
                    height={80}
                />
            )}
            <button
                onClick={onRemove}
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 z-10"
            >
                ×
            </button>
        </div>
    )
}

interface AddPhotoModalProps {
    isOpen: boolean
    onClose: () => void
    homeId?: string
    homeownerId?: string | number
    workOrderId?: string // Optional work order ID to pre-select
    onUploadSuccess?: () => void // Callback to refresh photos after successful upload
    initialFiles?: File[] // Files that were already selected before opening the modal
    uploadTags?: string[]
    homeownerName?: string
}

function AddPhotoModal({ isOpen, onClose, homeId, homeownerId, workOrderId, onUploadSuccess, initialFiles = EMPTY_FILE_ARRAY, uploadTags, homeownerName }: AddPhotoModalProps) {

    const [selectedWorkOrder, setSelectedWorkOrder] = useState<string>(workOrderId || "")
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [teamLeadWorkOrders, setTeamLeadWorkOrders] = useState<any[]>([])
    const galleryInputRef = useRef<HTMLInputElement>(null)
    const submittedRef = useRef(false)

    const { enqueuePhotos } = useUploadQueue()

    // Set initial files when modal opens with pre-selected files
    useEffect(() => {
        if (isOpen && initialFiles.length > 0) {
            setSelectedFiles(initialFiles)
        }
    }, [isOpen, initialFiles])

    const { session, profile } = useSession()

    // Fetch work orders where current user is team lead for this home
    useEffect(() => {
        const fetchTeamLeadWorkOrders = async () => {
            if (isOpen && session && profile && homeId && !workOrderId) {
                const homeIdNum = parseInt(homeId, 10)
                if (isNaN(homeIdNum)) return

                try {
                    const workOrdersResponse = await getAllWorkOrders({
                        homeId: homeIdNum,
                        limit: 1000,
                    })

                    if (
                        workOrdersResponse.success &&
                        workOrdersResponse.data?.workOrders
                    ) {
                        const filteredWorkOrders =
                            workOrdersResponse.data.workOrders.filter(
                                (workOrder: any) =>
                                    workOrder.teamLead?.id === profile.id &&
                                    workOrder.home?.id === homeIdNum
                            )

                        const activeWorkOrders = filteredWorkOrders
                            .filter(
                                (workOrder: any) =>
                                    workOrder.status === "Active"
                            )
                            .map((workOrder: any) => ({
                                id: workOrder.id,
                                orderNumber: workOrder.orderNumber,
                                workOrderType: workOrder.workOrderType,
                                createdAt: workOrder.createdAt,
                            }))
                            .sort(
                                (
                                    a: { createdAt: string },
                                    b: { createdAt: string }
                                ) =>
                                    new Date(b.createdAt).getTime() -
                                    new Date(a.createdAt).getTime()
                            )

                        setTeamLeadWorkOrders(activeWorkOrders)
                    }
                } catch (error) {
                    console.error(
                        "Error fetching team lead work orders:",
                        error
                    )
                }
            }
        }

        fetchTeamLeadWorkOrders()
    }, [isOpen, session, profile, homeId, workOrderId])

    const handleGalleryClick = () => {
        galleryInputRef.current?.click()
    }

    const validateFile = (file: File): string | null => {
        const fileName = file.name.toLowerCase()
        const lastDot = fileName.lastIndexOf('.')
        const fileExtension = lastDot >= 0 ? fileName.substring(lastDot) : ''
        
        const unsupportedVideoExtensions = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
        if (unsupportedVideoExtensions.includes(fileExtension)) {
            return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format. You can use free tools like HandBrake or VLC to convert your video.`
        }
        
        const unsupportedVideoMimeTypes = [
            'video/x-msvideo',
            'video/x-matroska',
            'video/x-flv',
            'video/x-ms-wmv',
            'video/3gpp',
        ]
        if (unsupportedVideoMimeTypes.includes(file.type.toLowerCase())) {
            return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format. You can use free tools like HandBrake or VLC to convert your video.`
        }
        
        const isImage = file.type.startsWith('image/')
        const isVideo = file.type.startsWith('video/')
        const isPdf = file.type === 'application/pdf' || fileExtension === '.pdf'

        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.heic', '.heif']
        const videoExtensions = ['.mov', '.mp4', '.webm', '.m4v', '.ts']

        const isImageByExtension = imageExtensions.includes(fileExtension)
        const isVideoByExtension = videoExtensions.includes(fileExtension)

        if (!isImage && !isVideo && !isPdf && !isImageByExtension && !isVideoByExtension) {
            return `File "${file.name}" is not a supported image, video, or PDF format`
        }

        const MAX_IMAGE_SIZE = 50 * 1024 * 1024
        const MAX_VIDEO_SIZE = 500 * 1024 * 1024
        const isActuallyImage = isImage || isImageByExtension
        const isActuallyVideo = isVideo || isVideoByExtension
        const isActuallyPdf = isPdf || fileExtension === '.pdf'
        const MAX_PDF_SIZE = 50 * 1024 * 1024

        if (isActuallyImage && file.size > MAX_IMAGE_SIZE) {
            return `Image "${file.name}" is too large. Maximum size is ${MAX_IMAGE_SIZE / 1024 / 1024}MB`
        }

        if (isActuallyVideo && file.size > MAX_VIDEO_SIZE) {
            return `Video "${file.name}" is too large. Maximum size is ${MAX_VIDEO_SIZE / 1024 / 1024}MB`
        }

        if (isActuallyPdf && file.size > MAX_PDF_SIZE) {
            return `PDF "${file.name}" is too large. Maximum size is ${MAX_PDF_SIZE / 1024 / 1024}MB`
        }

        if (isActuallyVideo) {
            const supportedVideoTypes = [
                'video/quicktime',
                'video/mp4',
                'video/webm',
                'video/x-m4v',
                'video/mp2t',
            ]
            const supportedExtensions = ['.mov', '.mp4', '.webm', '.m4v', '.ts']
            const isSupportedType = supportedVideoTypes.includes(file.type.toLowerCase())
            const isSupportedExtension = supportedExtensions.includes(fileExtension)
            
            if (!isSupportedType && !isSupportedExtension) {
                return `Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, M4V, or TS format. You can use free tools like HandBrake or VLC to convert your video.`
            }
        }

        return null
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (files) {
            const fileArray = Array.from(files)
            const validFiles: File[] = []
            const errors: string[] = []
            const currentTotalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0)
            const MAX_TOTAL_SIZE = 1024 * 1024 * 1024
            
            fileArray.forEach((file) => {
                if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
                    errors.push(`Total file size would exceed ${MAX_TOTAL_SIZE / 1024 / 1024}MB limit`)
                    return
                }
                const error = validateFile(file)
                if (error) {
                    errors.push(error)
                } else {
                    validFiles.push(file)
                }
            })
            
            if (errors.length > 0) {
                errors.forEach((error) => errorToast(error))
            }
            if (validFiles.length > 0) {
                setSelectedFiles((prev) => [...prev, ...validFiles])
            }
            event.target.value = ''
        }
    }

    const handleSubmit = async () => {
        if (submittedRef.current) return
        if (selectedFiles.length === 0) {
            errorToast("Please select at least one photo or video")
            return
        }

        if (!homeId) {
            errorToast("Home ID is required")
            return
        }

        const homeIdNum = typeof homeId === 'string' ? parseInt(homeId, 10) : homeId
        if (isNaN(homeIdNum)) {
            errorToast("Invalid home ID")
            return
        }

        const workOrderIdToUse = workOrderId 
            ? parseInt(workOrderId, 10)
            : (selectedWorkOrder 
                ? parseInt(selectedWorkOrder, 10) 
                : undefined)

        if (workOrderIdToUse && isNaN(workOrderIdToUse)) {
            errorToast("Invalid work order ID")
            return
        }

        try {
            const unsupportedVideoExtensions = ['.avi', '.mkv', '.flv', '.wmv', '.3gp', '.divx', '.xvid']
            const unsupportedVideoMimeTypes = [
                'video/x-msvideo',
                'video/x-matroska',
                'video/x-flv',
                'video/x-ms-wmv',
                'video/3gpp',
            ]
            
            for (const file of selectedFiles) {
                const fileName = file.name.toLowerCase()
                const lastDot = fileName.lastIndexOf('.')
                const fileExtension = lastDot >= 0 ? fileName.substring(lastDot) : ''
                
                if (unsupportedVideoExtensions.includes(fileExtension) || 
                    unsupportedVideoMimeTypes.includes(file.type.toLowerCase())) {
                    errorToast(`Video "${file.name}" format is not supported. Please convert to MP4, MOV, WebM, or M4V format. You can use free tools like HandBrake or VLC to convert your video.`)
                    return
                }
            }
            
            submittedRef.current = true
            
            const homeownerIdNum = homeownerId ? parseInt(String(homeownerId), 10) : undefined

            // Enqueue photos to IndexedDB queue manager
            await enqueuePhotos(
                homeIdNum,
                selectedFiles,
                workOrderIdToUse,
                uploadTags?.length ? uploadTags : undefined,
                undefined,
                homeownerIdNum,
                homeownerName
            )

            
            successToast(
                `Queued ${selectedFiles.length} photo(s) for background upload. You can close this modal or monitor progress in the navigation bar.`
            )

            setSelectedFiles([])
            if (onUploadSuccess) onUploadSuccess()
            onClose()
        } catch (error) {
            console.error("Error enqueuing photos:", error)
            errorToast(
                error instanceof Error 
                    ? error.message 
                    : "An error occurred while queueing photos. Please try again."
            )
        } finally {
            submittedRef.current = false
        }
    }

    const handleCancel = () => {
        setSelectedWorkOrder("")
        setSelectedFiles([])
        onClose()
    }

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
    }

    // Reset form when modal closes, but preserve workOrderId if provided
    useEffect(() => {
        if (!isOpen) {
            setSelectedWorkOrder(workOrderId || "")
            if (initialFiles.length === 0) {
                setSelectedFiles([])
            }
        }
    }, [isOpen, workOrderId, initialFiles])

    // Update selected work order when workOrderId prop changes
    useEffect(() => {
        if (workOrderId) {
            setSelectedWorkOrder(workOrderId)
        }
    }, [workOrderId])

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Add Photos</DialogTitle>
                    <DialogDescription>
                        Select photos or videos to associate with this home.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    {/* Work Order dropdown - only shown if workOrderId is not pre-specified and user has active team lead work orders */}
                    {!workOrderId && teamLeadWorkOrders.length > 0 && (
                        <div className="grid gap-2">
                            <Label htmlFor="work-order">Work Order (optional)</Label>
                            <Select
                                value={selectedWorkOrder}
                                onValueChange={setSelectedWorkOrder}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select work order" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No Work Order</SelectItem>
                                    {teamLeadWorkOrders.map((workOrder) => (
                                        <SelectItem key={workOrder.id} value={workOrder.id.toString()}>
                                            #{workOrder.orderNumber} - {workOrder.workOrderType}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Drag and Drop Area */}
                    <div
                        onClick={handleGalleryClick}
                        className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex flex-col items-center gap-2 text-gray-500">
                            <Image
                                src={galleryIcon}
                                alt="Gallery icon"
                                width={48}
                                height={48}
                                className="opacity-60"
                            />
                            <div className="flex flex-col items-center">
                                <span className="font-semibold">Click to upload</span>
                                <span className="text-sm">Select Photos</span>
                            </div>
                        </div>
                    </div>

                    {/* Hidden file input for gallery selection */}
                    <input
                        ref={galleryInputRef}
                        type="file"
                        accept="image/*,video/*,.pdf,application/pdf"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    {/* Selected files preview */}
                    {selectedFiles.length > 0 && (
                        <div className="space-y-2">
                            <Label>
                                Selected Media ({selectedFiles.length})
                            </Label>
                            <div className="grid grid-cols-3 gap-2 p-2 max-h-64 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                {selectedFiles.map((file, index) => {
                                    const isVideo = file.type.startsWith('video/')
                                    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
                                    return (
                                        <FilePreviewItem
                                            key={`${file.name}-${index}`}
                                            file={file}
                                            isVideo={isVideo}
                                            isPdf={isPdf}
                                            onRemove={() => removeFile(index)}
                                        />
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        className="w-full"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        className="w-full"
                        disabled={selectedFiles.length === 0}
                    >
                        Submit
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default AddPhotoModal
